import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSubscriber } from 'services/api/hooks';
import { Box, Typography } from '@mui/material';
import { LoadingGif } from 'common/animations/LoadingGif';
import { Button } from 'system/atoms';
import { OpeningMessagesTable } from './components/OpeningMessagesTable';
import { Loading } from 'components/Loading';
import {
  CampaignStatus,
  Campaign as CampaignType
} from 'services/campaign/types';
import { Paths } from 'services/navigation/paths';
import { onUpdateCampaign } from 'services/campaign/subsribtions';

import { useRecoilValue } from 'recoil';
import { isProductLimitReachedSelector } from 'services/billing/atoms';
import { useCampaignConversations } from 'services/conversations/hooks';
import {
  OpeningMessagesLaunchButtons,
  OpeningMessagesLaunchOption
} from './components/OpeningMessagesLaunchButtons';
import { ScheduleLaunchModal } from './components/ScheduleLaunchModal';
import { LaunchCountdownModal } from 'modules/campaign-creation/components/LaunchCountdownModal';
import { ConversationStatus } from 'services/conversations/types';
import { useCampaign } from 'services/campaign/hooks';
import { useCampaignStatusPolling } from 'services/campaign/useCampaignStatusPolling';
import { onboardingElementIds } from 'services/onboarding/constants';
import { Header } from 'common/ui/Header';
import { SubscriptionProduct } from 'generated-graphql/types';
import { useConfetti } from 'utils/confetti';
import { useToast, ToastType } from 'services/toast';
import { campaignOpeningMessagesSx } from './styles';

export const CampaignOpeningMessages = () => {
  const { id: campaignId } = useParams();
  const location = useLocation();

  // Check if we came from "Add Candidates" flow - don't show confetti in that case
  const navigationState = location.state as {
    isAddingCandidates?: boolean;
  } | null;
  const isAddingCandidates = navigationState?.isAddingCandidates ?? false;

  const navigate = useNavigate();
  const { showToast } = useToast();
  const { triggerConfetti } = useConfetti();
  const isLimitReachedForPoppConversations = useRecoilValue(
    isProductLimitReachedSelector(SubscriptionProduct.PoppConversations)
  );

  const {
    campaign,
    isLoading,
    setCampaign,
    launchCampaign,
    isLaunching,
    refetchCampaign
  } = useCampaign({
    campaignId: campaignId || ''
  });

  const { conversations, refetch: refetchConversations } =
    useCampaignConversations({
      campaignId,
      queryParams: {
        filter: {
          or: [
            {
              status: {
                eq: ConversationStatus.CREATED
              }
            }
          ]
        }
      }
    });

  // Modal states
  const [isCountdownModalOpen, setIsCountdownModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  useSubscriber({
    subQuery: onUpdateCampaign,
    subParams: {
      id: { eq: campaignId || '' }
    },
    onFetchSuccess: async (data) => {
      const campaignUpdated: CampaignType = data?.onUpdateCampaign;

      if (campaignUpdated?.status !== campaign?.status) {
        const isSending =
          campaignUpdated.status === CampaignStatus.SENDING_MESSAGES;
        const isScheduled = campaignUpdated.status === CampaignStatus.SCHEDULED;
        const isSent =
          campaignUpdated.status === CampaignStatus.FINISHED_SENDING;

        if (isSent || isScheduled || isSending) {
          navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`);
          return;
        }

        setCampaign((campaign) => ({
          ...campaign,
          ...campaignUpdated
        }));
      }
    }
  });

  useCampaignStatusPolling({
    campaignStatus: campaign?.status,
    totalConversationsCount: campaign?.totalConversationsCount ?? 0,
    totalProspectsCount: campaign?.totalProspectsCount ?? 0,
    refetchCampaign,
    refetchConversations
  });

  const isGenerating = campaign?.status === CampaignStatus.GENERATING_MESSAGES;
  const isFinishedGenerating =
    campaign?.status === CampaignStatus.FINISHED_GENERATING;
  const isFinishedSending =
    campaign?.status === CampaignStatus.FINISHED_SENDING;
  const isSent = campaign?.status === CampaignStatus.FINISHED_SENDING;
  const isProcessing = campaign?.status === CampaignStatus.PROCESSING;
  const isSending = campaign?.status === CampaignStatus.SENDING_MESSAGES;
  const totalProspectsCount = campaign?.totalProspectsCount || 0;
  const isFinishedGeneratingWithNoProspects =
    isFinishedGenerating && !totalProspectsCount;

  const footerPrimaryButtonText = useMemo(() => {
    if (isFinishedSending) return 'View campaign';
    if (isLimitReachedForPoppConversations) return 'Plan limit reached';
    return 'Launch campaign';
  }, [isFinishedSending, isLimitReachedForPoppConversations]);

  const footerPrimaryButtonId = useMemo(() => {
    if (isFinishedGenerating)
      return onboardingElementIds.launchCampaignReadyToSendButton;

    return '';
  }, [isFinishedGenerating]);

  const onPrimaryClick = useCallback(() => {
    if (isFinishedSending) {
      navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaign?.id}`);
    }
  }, [campaign?.id, navigate, isFinishedSending]);

  const shouldDisableLaunchButton = useMemo(() => {
    return (
      !isFinishedGenerating ||
      !totalProspectsCount ||
      isLimitReachedForPoppConversations
    );
  }, [
    isFinishedGenerating,
    totalProspectsCount,
    isLimitReachedForPoppConversations
  ]);

  // Handle launch option selection
  const handleSelectLaunchOption = useCallback(
    (option: OpeningMessagesLaunchOption) => {
      if (option === 'now') {
        // Open countdown modal
        setIsCountdownModalOpen(true);
      } else if (option === 'schedule') {
        // Open schedule modal
        setIsScheduleModalOpen(true);
      } else if (option === 'doNotLaunch') {
        // Show toast and navigate to the campaign page
        showToast({
          message: 'Your campaign is saved and ready to launch',
          type: ToastType.Success
        });
        if (campaignId) {
          navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`);
        } else {
          navigate(Paths.CAMPAIGNS);
        }
      }
    },
    [campaignId, navigate, showToast]
  );

  // Handle countdown complete - launch immediately
  const handleCountdownComplete = useCallback(async () => {
    setIsCountdownModalOpen(false);

    // Launch immediately (no schedule time)
    // The hook returns true on success, false on failure
    // It also handles showing the appropriate toast
    const success = await launchCampaign({
      launchScheduleDateTime: undefined
    });

    if (success) {
      // Only show confetti for new campaigns, not when adding candidates to existing ones
      if (!isAddingCandidates) {
        triggerConfetti();
      }

      if (campaignId) {
        navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`);
      } else {
        navigate(Paths.CAMPAIGNS);
      }
    }
  }, [
    campaignId,
    isAddingCandidates,
    launchCampaign,
    navigate,
    triggerConfetti
  ]);

  // Handle abort countdown - close modal and return to inline options
  const handleAbortCountdown = () => {
    setIsCountdownModalOpen(false);
  };

  // Handle schedule confirmation
  const handleScheduleConfirm = useCallback(
    (scheduledDateTime: string) =>
      launchCampaign({
        launchScheduleDateTime: scheduledDateTime
      }),
    [launchCampaign]
  );

  // Handle successful schedule - navigate away
  const handleScheduleSuccess = useCallback(() => {
    setIsScheduleModalOpen(false);
    if (campaignId) {
      navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`);
    } else {
      navigate(Paths.CAMPAIGNS);
    }
  }, [campaignId, navigate]);

  // Handle schedule cancel - close modal and return to inline options
  const handleScheduleClose = () => {
    setIsScheduleModalOpen(false);
  };

  if (isLoading && !campaign)
    return (
      <Box sx={campaignOpeningMessagesSx.loadingContainer}>
        <Loading />
      </Box>
    );

  if (isFinishedGeneratingWithNoProspects) {
    return (
      <Box>
        <Box sx={campaignOpeningMessagesSx.centerContainer}>
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={campaignOpeningMessagesSx.errorMessageText}
          >
            We could not find any candidate contact details in your CSV. Go to
            the dashboard to add another CSV.
          </Typography>
          <Button
            variant="secondary"
            onClick={() => {
              navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`);
            }}
          >
            Go to dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  if (!isLoading && !conversations?.length && isSent)
    return (
      <Box>
        <Box sx={campaignOpeningMessagesSx.sentMessageContainer}>
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={campaignOpeningMessagesSx.sentMessageText}
          >
            All conversations have been sent for this campaign
          </Typography>
          <Button
            variant="primary"
            onClick={() => {
              navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`);
            }}
          >
            Campaign dashboard
          </Button>
        </Box>
      </Box>
    );

  const campaignTitle = campaign?.title || 'Campaign';

  return (
    <>
      <Box id={onboardingElementIds.campaignOpeningMessagesContainer}>
        {/* Header with breadcrumb and title */}
        <Header
          breadcrumbs={[
            { name: 'CAMPAIGNS', path: Paths.CAMPAIGNS },
            {
              name: campaignTitle.toUpperCase(),
              path: `${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`
            }
          ]}
          currentBreadcrumb="OPENING MESSAGES"
          title="Personalising messages"
          actions={
            isFinishedSending ? (
              <Button
                variant="primary"
                onClick={onPrimaryClick}
                id={footerPrimaryButtonId}
                sx={campaignOpeningMessagesSx.launchButtonStyle}
              >
                {footerPrimaryButtonText}
              </Button>
            ) : !isFinishedSending && isFinishedGenerating ? (
              <OpeningMessagesLaunchButtons
                onSelectOption={handleSelectLaunchOption}
                disabled={shouldDisableLaunchButton || isLaunching || isSending}
              />
            ) : undefined
          }
        />

        {/* Loading state message */}
        {(isGenerating || isProcessing) && (
          <Box sx={campaignOpeningMessagesSx.loadingCardContainer}>
            <Box sx={campaignOpeningMessagesSx.loadingCard}>
              <LoadingGif width={24} height={24} />
              <Box>
                <Typography sx={campaignOpeningMessagesSx.loadingTitleStyle}>
                  Writing messages may take a minute or two
                </Typography>
                <Typography sx={campaignOpeningMessagesSx.loadingSubtitleStyle}>
                  We&apos;ll send you an email when your campaign is ready to
                  send.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Countdown Modal */}
        <LaunchCountdownModal
          isOpen={isCountdownModalOpen}
          onCountdownComplete={handleCountdownComplete}
          onAbort={handleAbortCountdown}
        />

        {/* Schedule Modal */}
        <ScheduleLaunchModal
          isOpen={isScheduleModalOpen}
          onClose={handleScheduleClose}
          onConfirm={handleScheduleConfirm}
          onSuccess={handleScheduleSuccess}
        />

        {/* Main content with 1/3 - 2/3 layout */}
        <Box sx={campaignOpeningMessagesSx.innerContainer}>
          {/* Left column - 1/3 */}
          <Box sx={campaignOpeningMessagesSx.leftColumn}>
            <Typography sx={campaignOpeningMessagesSx.sectionTitle}>
              Opening messages
            </Typography>
            <Typography sx={campaignOpeningMessagesSx.sectionSubtitle}>
              Review messages before launching.
            </Typography>
          </Box>

          {/* Right column - 2/3 */}
          <Box sx={campaignOpeningMessagesSx.rightColumn}>
            <OpeningMessagesTable conversations={conversations} />
          </Box>
        </Box>
      </Box>
    </>
  );
};
